/**
 * 테스트 시나리오 — 여유/혼잡/만차 버튼 하나로 입구 종합 알림판(층) 또는 단면 유도 전광판(칸)이
 * 그 표시가 나오게 주차면을 맞춘다. 표시 판정은 qaScenario.guideLevel (induce 와 같다).
 */
import React, { useMemo } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';

import type { QaBoard, QaSpace } from '../../services/SiteParkingQaService';
import {
  countsForEntrance,
  directionLabel,
  GUIDE_COLOR,
  GUIDE_LABEL,
  GuideLevel,
  guideLevel,
  planScenario,
} from './qaScenario';

interface Props {
  /** entrance: 입구 종합 알림판(층), board: 단면 유도 전광판(칸). */
  mode: 'entrance' | 'board';
  level: string;
  spaces: QaSpace[];
  boards: QaBoard[];
  pending: Set<number>;
  onApply: (plan: { occupy: number[]; vacate: number[] }, label: string) => void;
}

const LEVELS: GuideLevel[] = ['smooth', 'congested', 'full'];

/** 한 줄 — 이름, 지금 표시(예상), 여유/혼잡/만차 버튼. */
const ScenarioRow: React.FC<{
  title: string;
  sub?: string;
  spaces: QaSpace[];
  pending: Set<number>;
  testId: string;
  onApply: Props['onApply'];
}> = ({ title, sub, spaces, pending, testId, onApply }) => {
  const used = spaces.filter((s) => s.status === 'Y').length;
  const now = guideLevel(used, spaces.length);
  const busy = spaces.some((s) => pending.has(s.id));
  const pct = spaces.length ? Math.floor((used * 100) / spaces.length) : 0;
  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'grid',
        gap: 1,
        alignItems: 'center',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
        py: 1,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={700} noWrap>
            {title}
          </Typography>
          <Box
            component="span"
            data-testid={`${testId}-now`}
            sx={{
              px: 0.75,
              borderRadius: 1,
              fontSize: 12,
              fontWeight: 700,
              color: '#fff',
              bgcolor: GUIDE_COLOR[now],
              flexShrink: 0,
            }}
          >
            {GUIDE_LABEL[now]}
          </Box>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {sub ? `${sub} · ` : ''}점유 {used}/{spaces.length}면 ({pct}%)
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.75}>
        {LEVELS.map((lv) => {
          const plan = planScenario(spaces, lv);
          return (
            <Button
              key={lv}
              size="small"
              variant={now === lv ? 'contained' : 'outlined'}
              disableElevation
              disabled={!plan || busy}
              onClick={() => plan && onApply(plan, `${title} ${GUIDE_LABEL[lv]}`)}
              title={plan ? undefined : '면이 너무 적어 이 표시를 만들 수 없습니다'}
              data-testid={`${testId}-${lv}`}
              sx={{
                flex: { xs: 1, sm: 'none' },
                minWidth: 64,
                minHeight: 36,
                fontWeight: 700,
                color: now === lv ? '#fff' : GUIDE_COLOR[lv],
                borderColor: GUIDE_COLOR[lv],
                bgcolor: now === lv ? GUIDE_COLOR[lv] : undefined,
                // 테마의 contained 버튼 그라데이션을 덮어 표시 색(여유·혼잡·만차)이 보이게
                backgroundImage: 'none',
                '&:hover': { borderColor: GUIDE_COLOR[lv], bgcolor: `${GUIDE_COLOR[lv]}33` },
              }}
            >
              {GUIDE_LABEL[lv]}
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
};

const QaScenarioPanel: React.FC<Props> = ({ mode, level, spaces, boards, pending, onApply }) => {
  const byId = useMemo(() => new Map(spaces.map((s) => [s.id, s])), [spaces]);
  const entrance = useMemo(
    () => spaces.filter((s) => s.level === level && countsForEntrance(s)),
    [spaces, level],
  );
  // 이 층 주차면을 세는 전광판 칸만, 같은 IP(한 대의 전광판)끼리 묶는다.
  const boardGroups = useMemo(() => {
    const byIp = new Map<
      string,
      { title: string; sub: string; key: string; spaces: QaSpace[] }[]
    >();
    boards.forEach((b) =>
      b.groups.forEach((g) => {
        const list = g.spaceIds.map((id) => byId.get(id)).filter((s): s is QaSpace => !!s);
        if (!list.some((s) => s.level === level)) return;
        const rows = byIp.get(b.ip) ?? [];
        rows.push({
          title: `${b.name} · ${directionLabel(g.direction)}`,
          sub: `전광판 ${b.ip}`,
          key: `${b.id}-${g.direction}`,
          spaces: list,
        });
        byIp.set(b.ip, rows);
      }),
    );
    return Array.from(byIp.values()).flat();
  }, [boards, byId, level]);

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}
      data-testid="qa-scenario"
    >
      <Typography variant="subtitle1" fontWeight={700}>
        {mode === 'entrance' ? '입구 종합 알림판' : '단면 유도 전광판'} · {level}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
        {mode === 'entrance'
          ? '층 전체 점유율로 표시가 바뀝니다.'
          : '전광판 칸(왼쪽·직진·오른쪽)마다 그 칸이 세는 주차면 점유율로 표시가 바뀝니다.'}{' '}
        버튼을 누르면 그 표시가 나오도록 주차면을 맞춥니다 (여유 60% 이하, 혼잡 61~99%, 만차 100%).
      </Typography>

      {mode === 'entrance' &&
        (entrance.length ? (
          <ScenarioRow
            title={`${level} 층`}
            sub="CCTV 연결 일반·가족 면"
            spaces={entrance}
            pending={pending}
            testId="qa-entrance"
            onApply={onApply}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            이 층에 입구 알림판이 세는 주차면(CCTV 연결 일반·가족)이 없습니다.
          </Typography>
        ))}

      {mode === 'board' &&
        (boardGroups.length ? (
          boardGroups.map((r) => (
            <ScenarioRow
              key={r.key}
              title={r.title}
              sub={r.sub}
              spaces={r.spaces}
              pending={pending}
              testId={`qa-board-${r.key}`}
              onApply={onApply}
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            이 층 주차면을 세는 단면 유도 전광판이 없습니다.
          </Typography>
        ))}
    </Paper>
  );
};

export default QaScenarioPanel;
